import React, { useState, useRef, useEffect } from 'react';
import { Send, AlertTriangle } from 'lucide-react';
import { MessageInput as MessageInputType } from '../../types/chat';
import { chatService } from '../../lib/chat/chatService';

interface MessageInputProps {
  isAdmin?: boolean;
  tournamentId: string;
}

export function MessageInput({ isAdmin, tournamentId }: MessageInputProps) {
  const [content, setContent] = useState('');
  const [isBroadcast, setIsBroadcast] = useState(false);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('low');
  const [category, setCategory] = useState<string>('general');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isSubmittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    if (!tournamentId) return;
    if (isSubmittingRef.current) return;
    setError(null);
    
    isSubmittingRef.current = true;

    try {
      const message: MessageInputType = {
        content: content.trim(),
        type: isBroadcast ? 'broadcast' : 'chat',
        tournamentId,
        isAdmin,
        metadata: isBroadcast ? {
          priority,
          category,
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        } : undefined
      };

      await chatService.sendMessage(message);
      setContent('');
      
      if (isBroadcast) {
        setIsBroadcast(false);
        setPriority('low');
        setCategory('general');
      }
      
      // Clear input and reset state
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message. Please try again.');
    }
    finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t bg-white p-3 pb-3 space-y-3 fixed bottom-[calc(56px+env(safe-area-inset-bottom))] left-0 right-0 z-[10000]">
      {isAdmin && (
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isBroadcast}
              onChange={(e) => setIsBroadcast(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Broadcast
            </span>
          </label>

          {isBroadcast && (
            <>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as any)}
                className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>

              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="general">General</option>
                <option value="weather">Weather</option>
                <option value="lost-found">Lost & Found</option>
                <option value="social">Social</option>
              </select>
            </>
          )}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Message Input */}
      <div className="flex items-end gap-4">
        <textarea
          ref={inputRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={isBroadcast ? "Type broadcast message..." : "Type message..."}
          className="flex-1 resize-none rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500 min-h-[2.5rem] max-h-24 text-base"
          rows={Math.min(content.split('\n').length, 4)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <button
          type="submit"
          disabled={!content.trim() || isSubmittingRef.current}
          className="rounded-lg bg-primary-600 p-2.5 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </form>
  );
}